// Global packages and components
import { clsx } from 'clsx'
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { getProductPageLink } from '~/utils'
import { useNavigate, useRouteLoaderData } from '@remix-run/react'
import { LoaderIcon } from '~/components'

// Types
import type { BrendaHeaderSearchProduct, RootDataProps } from '~/types'

interface Props {
  loading: boolean
  featured?: boolean
  hasFocus?: boolean
  popular?: string[]
  suggestions?: { suggest: TrustedHTML }[]
  products?: BrendaHeaderSearchProduct[]
  onClick: (e: string) => void
  setHasFocus: (e: boolean) => void
}

// Main export
const SearchDropdown = ({
  loading,
  featured,
  hasFocus,
  popular,
  suggestions,
  products,
  onClick,
  setHasFocus,
}: Props) => {
  const navigate = useNavigate()
  const { productOverrides } = useRouteLoaderData('root') as RootDataProps

  return (
    <div
      className={clsx(
        'absolute -left-[10px] -top-[10px] w-[calc(100%+20px)] md:-left-[15px] md:-top-[15px] md:w-[calc(100%+30px)]',
        {
          hidden: !hasFocus,
          block: hasFocus,
        }
      )}
    >
      <div
        className={clsx(
          'w-full rounded-xl bg-neutral-50 px-5 pb-5 pt-20 text-left shadow-md',
          {
            'md:pt-28': featured,
          }
        )}
      >
        <div className="max-h-64 overflow-y-auto pr-5">
          {loading ? (
            <div className="flex w-full items-center justify-center px-2 py-4">
              <LoaderIcon size="sm" />
            </div>
          ) : (!suggestions || suggestions?.length == 0) &&
            (!products || products?.length == 0) ? (
            <div>
              {popular && popular?.length > 0 ? (
                <>
                  <h5 className="mb-3 font-semibold">Popular searches</h5>
                  <ul>
                    {popular.map((e, i) => (
                      <li
                        key={i}
                        className="mb-1"
                      >
                        <button
                          className="pointer-events-auto flex w-full items-center hover:bg-gray-100"
                          onMouseDown={() => {
                            setHasFocus(false)
                            onClick(e)
                          }}
                        >
                          <MagnifyingGlassIcon className="mr-1 size-4" />
                          {e}
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="mb-0 text-sm">
                  Type a search term to see suggested results
                </p>
              )}
            </div>
          ) : (
            <>
              {suggestions && suggestions?.length > 0 && (
                <div
                  className={clsx({
                    'mb-6': products && products?.length > 0,
                  })}
                >
                  <h5 className="mb-4 font-semibold">Suggestions</h5>
                  <ul>
                    {suggestions.map((e, i) => (
                      <li
                        key={i}
                        className="mb-1"
                      >
                        <button
                          className="flex w-full items-start justify-start rounded-sm text-left hover:bg-gray-100"
                          onMouseDown={() => {
                            setHasFocus(false)
                            onClick(
                              e.suggest
                                .toString()
                                .replace(/<\/?[^>]+(>|$)/g, '') as string
                            )
                          }}
                        >
                          <MagnifyingGlassIcon className="mr-1 size-5 pt-1" />
                          <span
                            dangerouslySetInnerHTML={{
                              __html: e.suggest,
                            }}
                          />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {products && products?.length > 0 && (
                <div>
                  <h5 className="mb-4 font-semibold">Products</h5>
                  <ul>
                    {products.slice(0, 6).map((e, i) => {
                      // Find the override object for this stock code
                      const override = productOverrides?.find(o =>
                        o.productCodes?.includes(e?.parentProduct?.stockCode)
                      )
                      const buttonText = override?.buttonTextShort ?? null
                      return (
                        <li
                          key={i}
                          className="mb-2 cursor-pointer"
                        >
                          <button
                            onMouseDown={() => {
                              setHasFocus(false)
                              navigate(
                                getProductPageLink({
                                  productType: e.hyraxProductType,
                                  stockCode: e.parentProduct.stockCode,
                                  slug: e.parentProduct.slug,
                                  variant: e?.option?.value,
                                })
                              )
                            }}
                          >
                            <div className="flex items-start gap-4">
                              <div className="relative flex aspect-square w-20 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white">
                                <div>
                                  {e?.images &&
                                    e.images?.length > 0 &&
                                    e.images[0]?.imageUrl && (
                                      <img
                                        src={e.images[0].imageUrl}
                                        alt={e.name + ' image'}
                                        className="max-h-full max-w-full"
                                      />
                                    )}
                                </div>
                              </div>
                              <div className="pt-1 text-left">
                                <h5 className="mb-1">{e.name}</h5>
                                <p className="text-sm font-semibold">
                                  {buttonText ??
                                    (e.hyraxProductType == 'bulk'
                                      ? 'Hire'
                                      : 'Buy')}
                                </p>
                              </div>
                            </div>
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default SearchDropdown
